/**
 * Auth Types - Shared type definitions for authentication
 * LAYER-IDENTITY-ACCESS
 *
 * These types mirror the backend Role enum and user entity structure.
 * Keep in sync with graphql-svc/src/entities/user.ts
 */

/**
 * User roles - matches backend Role enum
 * Order matters for hierarchy: higher roles inherit lower role permissions
 */
export enum Role {
  CUSTOMER = 'CUSTOMER',
  CASE_MANAGER = 'CASE_MANAGER',
  COMPLIANCE = 'COMPLIANCE',
  ADMIN = 'ADMIN',
  SUPER_ADMIN = 'SUPER_ADMIN',
}

/**
 * Role hierarchy - defines which roles inherit from others.
 * ADMIN inherits CASE_MANAGER and COMPLIANCE permissions.
 * SUPER_ADMIN inherits everything.
 */
export const ROLE_HIERARCHY: Record<Role, Role[]> = {
  [Role.CUSTOMER]: [],
  [Role.CASE_MANAGER]: [],
  [Role.COMPLIANCE]: [],
  [Role.ADMIN]: [Role.CASE_MANAGER, Role.COMPLIANCE],
  [Role.SUPER_ADMIN]: [Role.ADMIN, Role.CASE_MANAGER, Role.COMPLIANCE],
};

/**
 * User status - matches backend UserStatus enum
 */
export enum UserStatus {
  PENDING = 'PENDING',
  VERIFYING = 'VERIFYING',
  PREACTIVE = 'PREACTIVE',
  ACTIVE = 'ACTIVE',
  LOCKED = 'LOCKED',
  DELETED = 'DELETED',
  ACTIVE_NO_PHONE = 'ACTIVE_NO_PHONE',
  UNVERIFIED = 'UNVERIFIED',
  PENDING_REVIEW = 'PENDING_REVIEW',
}

/**
 * Authenticated user info returned from the API
 */
export interface AuthUser {
  uuid: string;
  email: string;
  firstName?: string;
  lastName?: string;
  roles: Role[];
  status: UserStatus;
  isEmailVerified: boolean;
}

/**
 * Authentication state
 */
export interface AuthState {
  /** Current authenticated user, or null if not authenticated */
  user: AuthUser | null;
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /** Whether authentication is being initialized or refreshed */
  isLoading: boolean;
  /** Access token stored in memory only (not localStorage) */
  accessToken: string | null;
}

/**
 * Check if a user has a specific role (with hierarchy support)
 * @param userRoles - The user's assigned roles
 * @param requiredRole - The role to check for
 * @returns true if user has the role directly or through hierarchy
 */
export function hasRole(userRoles: Role[], requiredRole: Role): boolean {
  return userRoles.some(
    (role) => role === requiredRole || ROLE_HIERARCHY[role]?.includes(requiredRole)
  );
}

/**
 * Get portal access for a role
 * @param role - The user's primary role
 * @returns Array of portal IDs the role can access
 */
export function getRolePortalAccess(role: Role): string[] {
  switch (role) {
    case Role.CUSTOMER:
      return ['customer'];
    case Role.CASE_MANAGER:
      return ['case-manager'];
    case Role.COMPLIANCE:
      return ['compliance'];
    case Role.ADMIN:
      return ['admin', 'case-manager', 'compliance'];
    case Role.SUPER_ADMIN:
      return ['admin', 'case-manager', 'compliance', 'customer'];
    default:
      return [];
  }
}

/**
 * Get the user's display name
 * @param user - The authenticated user
 * @returns Full name or email if name not available
 */
export function getUserDisplayName(user: AuthUser | null): string {
  if (!user) return '';
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  if (user.firstName) return user.firstName;
  return user.email;
}
